import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Clock, Users, TrendingUp, Edit } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import { useEnrichmentVelocityReport } from '@/hooks/useAnalyticsReports';
import { config } from '@/config/config';
import ReportFilters from './components/filters/ReportFilters';
import type { ReportFiltersState } from './components/filters/ReportFilters';
import { useReportspageTranslations } from '@/hooks/useReportspageTranslations';

const COLORS = config.reports.display.chartColors;

interface EnrichmentVelocityReportProps {
  filters?: ReportFiltersState;
}

const EnrichmentVelocityReport: React.FC<EnrichmentVelocityReportProps> = ({ filters = {} }) => {
  const [localFilters, setLocalFilters] = useState<ReportFiltersState>(filters);
  const { data, isLoading, error } = useEnrichmentVelocityReport();

  // Get translations
  const i18n = useReportspageTranslations();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-8 w-16" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="size-4" />
        <AlertDescription>
          {i18n.errors.velocity.failedToLoad(
            error instanceof Error ? error.message : i18n.errors.velocity.unknownError
          )}
        </AlertDescription>
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert>
        <AlertCircle className="size-4" />
        <AlertDescription>{i18n.errors.velocity.noDataAvailable}</AlertDescription>
      </Alert>
    );
  }

  const editCount = data.edit_count || 0;
  const avgTimeToPublish = data.avg_time_to_publish || 0;
  const productsEnriched = data.products_enriched || 0;
  const velocityTrend = data.velocity_trend || [];
  const topEditors = data.top_editors || [];

  // Format time to publish using translation
  const formatTime = (hours: number) => {
    if (hours < 1) {
      return i18n.formatTime.minutes(hours * 60);
    } else if (hours < 24) {
      return i18n.formatTime.hours(hours);
    } else {
      return i18n.formatTime.days(hours / 24);
    }
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <ReportFilters
        filters={localFilters}
        onFiltersChange={setLocalFilters}
        availableFilters={['locale', 'channel', 'family']}
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  {i18n.summaryCards.velocity.totalEdits}
                </p>
                <p className="text-2xl font-bold">{editCount.toLocaleString()}</p>
              </div>
              <Edit className="size-5 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  {i18n.summaryCards.velocity.avgTimeToPublish}
                </p>
                <p className="text-2xl font-bold">{formatTime(avgTimeToPublish)}</p>
              </div>
              <Clock className="size-5 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  {i18n.summaryCards.velocity.productsEnriched}
                </p>
                <p className="text-2xl font-bold">{productsEnriched.toLocaleString()}</p>
              </div>
              <TrendingUp className="size-5 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  {i18n.summaryCards.velocity.activeEditors}
                </p>
                <p className="text-2xl font-bold">{topEditors.length}</p>
              </div>
              <Users className="size-5 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Velocity Trend */}
        <Card>
          <CardHeader>
            <CardTitle>{i18n.charts.velocity.enrichmentVelocityTrend}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {velocityTrend.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={velocityTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={value => new Date(value).toLocaleDateString()}
                    />
                    <YAxis />
                    <Tooltip
                      labelFormatter={value => new Date(value).toLocaleDateString()}
                      formatter={(value, name) => [
                        name === 'edit_count' ? value : formatTime(Number(value)),
                        name === 'edit_count'
                          ? i18n.charts.velocity.edits
                          : i18n.charts.velocity.avgTimeToPublish,
                      ]}
                    />
                    <Line
                      type="monotone"
                      dataKey="edit_count"
                      stroke={COLORS[0]}
                      strokeWidth={2}
                      name="edit_count"
                    />
                    <Line
                      type="monotone"
                      dataKey="avg_time_to_publish"
                      stroke={COLORS[1]}
                      strokeWidth={2}
                      name="avg_time_to_publish"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full items-center justify-center text-gray-500">
                  {i18n.errors.velocity.noTrendData}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top Editors */}
        <Card>
          <CardHeader>
            <CardTitle>{i18n.charts.velocity.topEditorsBreakdown}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {topEditors.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={topEditors.slice(0, 10)} layout="horizontal">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="user" type="category" width={100} />
                    <Tooltip formatter={value => [value, i18n.charts.velocity.edits]} />
                    <Bar dataKey="edit_count" fill={COLORS[0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full items-center justify-center text-gray-500">
                  {i18n.errors.velocity.noEditorsData}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Products Enriched Trend */}
      {velocityTrend.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{i18n.charts.velocity.productsEnrichedOverTime}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={velocityTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={value => new Date(value).toLocaleDateString()}
                  />
                  <YAxis />
                  <Tooltip
                    labelFormatter={value => new Date(value).toLocaleDateString()}
                    formatter={value => [value, i18n.summaryCards.velocity.productsEnriched]}
                  />
                  <Bar dataKey="products_enriched" fill={COLORS[2] || COLORS[0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Editor Performance Table */}
      {topEditors.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{i18n.charts.velocity.editorPerformance}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="p-2 text-left">{i18n.velocityTable.headers.rank}</th>
                    <th className="p-2 text-left">{i18n.velocityTable.headers.editor}</th>
                    <th className="p-2 text-right">{i18n.velocityTable.headers.editCount}</th>
                    <th className="p-2 text-right">{i18n.velocityTable.headers.performance}</th>
                  </tr>
                </thead>
                <tbody>
                  {topEditors.slice(0, 10).map((editor, index) => (
                    <tr key={editor.user} className="border-b">
                      <td className="p-2">
                        <Badge variant={index < 3 ? 'default' : 'secondary'}>#{index + 1}</Badge>
                      </td>
                      <td className="p-2 font-medium">{editor.user}</td>
                      <td className="p-2 text-right">{editor.edit_count.toLocaleString()}</td>
                      <td className="p-2 text-right">
                        <Badge
                          variant={
                            editor.edit_count > 100
                              ? 'default'
                              : editor.edit_count > 50
                                ? 'secondary'
                                : 'outline'
                          }
                        >
                          {editor.edit_count > 100
                            ? i18n.velocityTable.performance.high
                            : editor.edit_count > 50
                              ? i18n.velocityTable.performance.medium
                              : i18n.velocityTable.performance.low}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EnrichmentVelocityReport;
